import { answersMatch } from './sampleMock';
import { toDate } from './progress';

export function stemKey(question) {
  const stem = String(question?.question || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .slice(0, 160);
  return stem ? `stem:${stem}` : '';
}

export function questionKey(question) {
  if (!question) return '';
  if (question.id != null && String(question.id).trim() !== '') return String(question.id);
  return stemKey(question);
}

export function questionKeys(question) {
  if (!question) return [];
  const keys = [];
  if (question.id != null && String(question.id).trim() !== '') keys.push(String(question.id));
  if (question.key) keys.push(String(question.key));
  const stem = stemKey(question);
  if (stem) keys.push(stem);
  return [...new Set(keys.filter(Boolean))];
}

export function stemPreview(question, max = 140) {
  const text = String(question?.question || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return '(no stem)';
  return text.length <= max ? text : `${text.slice(0, max)}…`;
}

export function stemFingerprint(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 80);
}

export function fingerprintsMatch(a, b) {
  if (!a || !b || a.length < 24 || b.length < 24) return false;
  if (a === b) return true;
  const left = a.slice(0, 40);
  const right = b.slice(0, 40);
  return a.startsWith(right) || b.startsWith(left);
}

export function isStemKey(value) {
  return String(value || '').startsWith('stem:');
}

export function isBankQuestionId(value) {
  const id = String(value || '').trim();
  return Boolean(id) && !isStemKey(id) && !id.startsWith('q_');
}

export function questionIndexDocId(question) {
  const id = question?.id != null ? String(question.id).trim() : '';
  if (isBankQuestionId(id)) return question?.questionVersion ? `${id}__${question.questionVersion}` : id;
  const key = questionKey(question);
  return key ? firestoreQuestionId(key) : '';
}

export function combineQuestionStats(rows = []) {
  const list = rows.filter(Boolean);
  if (!list.length) return null;
  const seenOf = (row) => Number(row.seen) || 0;
  const timeOf = (row) => Number(row.timeCount) || 0;
  const lastOf = (row) => toDate(row.lastEnd)?.getTime() || 0;
  const primary = list.reduce((best, row) => (seenOf(row) > seenOf(best) ? row : best));
  const latest = list.reduce((best, row) => (lastOf(row) > lastOf(best) ? row : best));
  const timed = list.reduce((best, row) => (timeOf(row) > timeOf(best) ? row : best));
  const timeCount = timeOf(timed);
  const answered = Number(primary.answered) || 0;
  return {
    ...primary,
    lastEnd: latest.lastEnd,
    timeSum: Number(timed.timeSum) || 0,
    timeCount,
    avgSeconds: timeCount ? Number(timed.timeSum) / timeCount : null,
    correctPct: answered ? ((Number(primary.correct) || 0) / answered) * 100 : primary.correctPct ?? null,
  };
}

export function matchBankQuestion(bank = [], { id, question, param } = {}) {
  const byId = (value) => {
    const needle = String(value || '').trim();
    if (!needle) return null;
    return bank.find((item) => String(item.id) === needle || questionKeys(item).includes(needle)) || null;
  };
  const direct = byId(id) || byId(param);
  if (direct) return direct;
  const fp = stemFingerprint(question);
  if (fp.length < 24) return null;
  return (
    bank.find((item) => fingerprintsMatch(fp, stemFingerprint(item.question))) || null
  );
}

export function emptyQuestionStat(question = {}) {
  return {
    key: questionKey(question),
    id: question.id ?? null,
    questionVersion: question.questionVersion || '',
    question: question.question || '',
    options: question.options || [],
    correctAnswer: question.correctAnswer,
    topic: question.topic || '',
    explanation: question.explanation || '',
    difficultyLevel: question.difficultyLevel || '',
    seen: 0,
    answered: 0,
    correct: 0,
    incorrect: 0,
    unanswered: 0,
    timeSum: 0,
    timeCount: 0,
    uniqueUids: new Set(),
    lastEnd: null,
    optionCounts: {},
  };
}

const hasAnswer = (value) => value !== null && value !== undefined && value !== '';

export function questionSecondsOf(attempt, index) {
  const times = attempt?.questionSeconds;
  if (!Array.isArray(times)) return null;
  const value = Number(times[index]);
  return Number.isFinite(value) && value > 0 ? Math.round(value * 1000) / 1000 : null;
}

export function aggregateQuestionStats(attempts = []) {
  const byKey = {};

  attempts.forEach((attempt) => {
    const questions = attempt.questions || [];
    const answers = attempt.answers || [];
    questions.forEach((question, index) => {
      const key = questionKey(question);
      if (!key) return;
      const bucket = `${key}__${question.questionVersion || ''}`;
      const cur = byKey[bucket] || emptyQuestionStat(question);
      if (!cur.question && question.question) Object.assign(cur, emptyQuestionStat({ ...cur, ...question }));
      cur.seen += 1;
      if (attempt.uid) cur.uniqueUids.add(attempt.uid);
      const answered = hasAnswer(answers[index]);
      if (!answered) {
        cur.unanswered += 1;
      } else {
        cur.answered += 1;
        const ok = answersMatch(answers[index], question.correctAnswer);
        if (ok) cur.correct += 1;
        else cur.incorrect += 1;
        const opt = String(answers[index]);
        cur.optionCounts[opt] = (cur.optionCounts[opt] || 0) + 1;
      }
      const seconds = questionSecondsOf(attempt, index);
      if (seconds != null) {
        cur.timeSum += seconds;
        cur.timeCount += 1;
      }
      const end = toDate(attempt.endTime);
      if (end && (!cur.lastEnd || end > cur.lastEnd)) cur.lastEnd = end;
      byKey[bucket] = cur;
    });
  });

  return Object.values(byKey).map((row) => ({
    ...row,
    uniqueUsers: row.uniqueUids.size,
    avgSeconds: row.timeCount ? row.timeSum / row.timeCount : null,
    correctPct: row.answered ? (row.correct / row.answered) * 100 : null,
  }));
}

export function mergeBankWithStats(bank = [], stats = []) {
  const claimed = new Set();
  const byAnyKey = new Map();
  const byFingerprint = [];
  stats.forEach((row) => {
    questionKeys(row).forEach((key) => {
      if (!byAnyKey.has(key)) byAnyKey.set(key, []);
      byAnyKey.get(key).push(row);
    });
    const fp = stemFingerprint(row.question || row.stem || '');
    if (fp.length >= 24) byFingerprint.push({ fp, row });
  });

  const rows = bank.map((question) => {
    const keyed = questionKeys(question)
      .flatMap((key) => byAnyKey.get(key) || [])
      .filter((row) => row && !claimed.has(row));
    const qfp = stemFingerprint(question.question);
    const fuzzy = byFingerprint
      .filter(({ fp, row }) => !claimed.has(row) && fingerprintsMatch(qfp, fp))
      .map(({ row }) => row);
    const candidates = [...new Set([...keyed, ...fuzzy])]
      .filter(row => (row.questionVersion || '') === (question.questionVersion || ''));
    const fromAttempts = combineQuestionStats(candidates);
    candidates.forEach((row) => claimed.add(row));
    if (fromAttempts) {
      const bankRow = emptyQuestionStat(question);
      const key = bankRow.key || fromAttempts.key;
      return {
        ...fromAttempts,
        ...bankRow,
        ...fromAttempts,
        key,
        id: question.id ?? fromAttempts.id,
        indexId: fromAttempts.indexId || questionIndexDocId(question) || firestoreQuestionId(key),
        question: question.question || fromAttempts.question,
        options: question.options || fromAttempts.options,
        correctAnswer: question.correctAnswer ?? fromAttempts.correctAnswer,
        topic: question.topic || fromAttempts.topic,
        inBank: true,
      };
    }
    return {
      ...emptyQuestionStat(question),
      uniqueUsers: 0,
      avgSeconds: null,
      correctPct: null,
      inBank: true,
      indexId: questionIndexDocId(question),
    };
  });

  stats.forEach((row) => {
    if (!claimed.has(row)) {
      rows.push({
        ...row,
        inBank: false,
        indexId: row.indexId || (isBankQuestionId(row.id) ? String(row.id) : firestoreQuestionId(row.key)),
      });
    }
  });
  rows.sort((a, b) => b.seen - a.seen || String(a.topic).localeCompare(String(b.topic)));
  return rows;
}

export function firestoreQuestionId(key) {
  const source = String(key || '');
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = (hash * 31 + source.charCodeAt(i)) >>> 0;
  }
  const tail = source.replace(/[^a-zA-Z0-9]/g, '').slice(0, 36);
  return `q_${hash.toString(36)}_${tail || 'item'}`;
}

export function questionStatsSlug(row) {
  if (!row) return '';
  if (row.inBank && isBankQuestionId(row.id)) return String(row.id).trim();
  const id = row.id != null ? String(row.id).trim() : '';
  if (isBankQuestionId(id)) return id;
  if (row.indexId) return String(row.indexId);
  const key = String(row.key || '').trim();
  if (isBankQuestionId(key)) return key;
  if (key && !isStemKey(key)) return key;
  return key ? firestoreQuestionId(key) : '';
}

export function questionStatsPath(row) {
  const slug = questionStatsSlug(row);
  return slug ? `/admin/questions/${encodeURIComponent(slug)}` : '/admin/questions';
}

export function questionIndexDocIds(param, question) {
  const ids = [];
  const push = (value) => {
    if (value && !ids.includes(value)) ids.push(value);
  };
  const raw = String(param || '').trim();
  push(questionIndexDocId(question));
  push(raw);
  if (isBankQuestionId(raw)) push(firestoreQuestionId(raw));
  if (question?.indexId) push(question.indexId);
  if (isBankQuestionId(question?.id)) push(String(question.id).trim());
  questionKeys(question).forEach((key) => {
    push(key);
    if (isBankQuestionId(key) || (!String(key).startsWith('q_') && !isStemKey(key))) {
      push(firestoreQuestionId(key));
    }
  });
  return ids.filter(Boolean);
}

export function finalizeIndexRow(data = {}) {
  const seen = Number(data.seen) || 0;
  const answered = Number(data.answered) || 0;
  const correct = Number(data.correct) || 0;
  const timeSum = Number(data.timeSum) || 0;
  const timeCount = Number(data.timeCount) || 0;
  return {
    key: data.key || '',
    id: data.id ?? null,
    questionVersion: data.questionVersion || '',
    indexId: data.indexId || (data.key ? firestoreQuestionId(data.key) : ''),
    question: data.question || '',
    options: data.options || [],
    correctAnswer: data.correctAnswer,
    topic: data.topic || '',
    seen,
    answered,
    correct,
    incorrect: Number(data.incorrect) || 0,
    unanswered: Number(data.unanswered) || 0,
    timeSum,
    timeCount,
    lastEnd: toDate(data.lastEnd),
    optionCounts: data.optionCounts && typeof data.optionCounts === 'object' ? data.optionCounts : {},
    uniqueUsers: Number(data.uniqueUsers) || 0,
    avgSeconds: timeCount ? timeSum / timeCount : null,
    correctPct: answered ? (correct / answered) * 100 : null,
  };
}

export function serializeQuestionRows(rows = []) {
  return rows.map((row) => ({
    key: row.key,
    id: row.id ?? null,
    questionVersion: row.questionVersion || '',
    indexId: row.indexId || (row.key ? firestoreQuestionId(row.key) : ''),
    question: String(row.question || '').slice(0, 280),
    topic: row.topic || '',
    seen: row.seen || 0,
    answered: row.answered || 0,
    correct: row.correct || 0,
    incorrect: row.incorrect || 0,
    unanswered: row.unanswered || 0,
    timeSum: row.timeSum || 0,
    timeCount: row.timeCount || 0,
    lastEnd: row.lastEnd ? new Date(row.lastEnd).toISOString() : null,
    optionCounts: row.optionCounts || {},
    uniqueUsers: row.uniqueUsers || 0,
    avgSeconds: row.avgSeconds ?? null,
    correctPct: row.correctPct ?? null,
    inBank: !!row.inBank,
    correctAnswer: row.correctAnswer,
  }));
}

export function hydrateQuestionRows(rows = []) {
  return rows.map((row) => finalizeIndexRow(row));
}

export function questionMatchesTarget(question, target = {}) {
  if (!question || !target) return false;
  if (target.questionVersion && question.questionVersion !== target.questionVersion) return false;
  const targetKeys = new Set(
    [
      ...questionKeys(target),
      target.id != null ? String(target.id).trim() : '',
      target.param ? String(target.param).trim() : '',
    ].filter(Boolean)
  );
  if (questionKeys(question).some((key) => targetKeys.has(key))) return true;
  if (isBankQuestionId(target.id) && String(question.id || '').trim() === String(target.id).trim()) {
    return true;
  }
  return fingerprintsMatch(stemFingerprint(question.question), stemFingerprint(target.question));
}

export function attemptHitsForQuestion(attempts = [], target = {}) {
  const hits = [];
  attempts.forEach((attempt) => {
    (attempt.questions || []).forEach((question, index) => {
      if (!questionMatchesTarget(question, target)) return;
      const answered = hasAnswer(attempt.answers?.[index]);
      hits.push({
        attemptId: attempt.key || attempt.id,
        uid: attempt.uid || '',
        name: attempt.name || '',
        email: attempt.email || '',
        endTime: attempt.endTime || attempt.createdAt,
        topic: attempt.topic || '',
        kind: attempt.kind || '',
        source: attempt.source || (attempt.uid ? 'account' : 'guest'),
        score: attempt.score,
        index,
        answer: attempt.answers?.[index],
        correct: answered ? answersMatch(attempt.answers?.[index], question.correctAnswer) : null,
        seconds: questionSecondsOf(attempt, index),
        question,
      });
    });
  });
  return hits.sort((a, b) => (toDate(b.endTime)?.getTime() || 0) - (toDate(a.endTime)?.getTime() || 0));
}
